/**
 * Sanitises text input for use with `latin1_swedish_ci` collation.
 *
 * IMPORTANT:
 * This utility keeps � and other unsupported visible characters.
 * These must be handled by validation (`validateLatin1SafeInput`).
 */

import { LATIN1_MAX_CODEPOINT, REPLACEMENT_CHARACTER, UNICODE_INVISIBLE_RE, UNICODE_WHITESPACE_RE } from './constants';
import type { MaybeString } from './latin1SafeTextField.types';
import { getCodePoint, isControlCharacter } from './utils';

type SanitizeResult = {
  value: string;
  didSanitize: boolean;
  didNormalize: boolean;
};

export const SPACE = ' ';
export const NEWLINE = '\n';
const NEWLINE_CODEPOINT = 0x0a; // "\n"
const TAB_CODEPOINT = 0x09; // "\t"
const REPLACEMENT_CHARACTER_CODEPOINT = 0xfffd;

// Smart typography characters -> closest ASCII equivalents
export const TYPOGRAPHY_REPLACEMENTS = new Map<number, string>([
  // Curly single quotes -> '
  [0x2018, "'"],
  [0x2019, "'"],
  [0x201a, "'"],
  [0x2032, "'"],

  // Curly double quotes -> "
  [0x201c, '"'],
  [0x201d, '"'],
  [0x201e, '"'],
  [0x2033, '"'],

  // Dashes / minus sign -> -
  [0x2010, '-'],
  [0x2011, '-'],
  [0x2012, '-'],
  [0x2013, '-'], // en dash
  [0x2014, '-'], // em dash
  [0x2015, '-'],
  [0x2212, '-'], // minus sign

  // Ellipsis -> ...
  [0x2026, '...'],

  // Bullets -> asterisk
  [0x2022, '*'],
  [0x2043, '*'],
  [0x2219, '*'],
]);

// Known legacy copy/paste artefacts -> closest ASCII equivalents
export const LEGACY_PASTE_REPLACEMENTS = new Map<number, string>([
  // Newline -> "\n"
  [0x0085, NEWLINE], // NEL (Next Line)
  [0x2028, NEWLINE], // Line Separator
  [0x2029, NEWLINE], // Paragraph Separator

  // Bullet-like glyphs from copy/paste  -> *
  [0x00b7, '* '], // Middle dot
  [0x25cf, '* '], // Black circle
  [0x25e6, '* '], // White bullet
  [0x2023, '* '], // Triangular bullet
  [0x204c, '* '], // Black leftwards bullet
  [0x204d, '* '], // Black rightwards bullet

  // Bullet-like glyphs from symbol fonts (e.g. Wingdings) -> *
  [0xf0b7, '* '],
  [0xf0a7, '* '],
  [0xf076, '* '],

  // Unicode mathematical operators -> ASCII equivalent
  [0x00d7, 'x'], // multiplication sign
  [0x2215, '/'], // divide slash
  [0x2044, '/'], // fraction slash
]);
// Normalise line endings, NFC normalisation, and trim leading/trailing whitespace
const normalizeInputFormatting = (input: string): string => input.replace(/\r\n?/g, NEWLINE).normalize('NFC').trim();
const getTypographyReplacement = (codePoint: number): string | undefined => TYPOGRAPHY_REPLACEMENTS.get(codePoint);
const getLegacyPasteReplacement = (codePoint: number): string | undefined => LEGACY_PASTE_REPLACEMENTS.get(codePoint);
const sanitizeNonLatin1Character = (inputChar: string): string | null => {
  // Replace remaining non-latin1 whitespace
  if (UNICODE_WHITESPACE_RE.test(inputChar)) {
    return SPACE;
  }

  // Remove remaining non-latin1 invisible characters
  if (UNICODE_INVISIBLE_RE.test(inputChar)) {
    return null;
  }

  return inputChar;
};

// Sanitises a Unicode code point, replacing or removing it if needed
export const sanitizeCharacter = (inputChar: string): string | null => {
  const codePoint = getCodePoint(inputChar);
  if (codePoint === undefined) {
    return null;
  }

  // Keep newline
  if (codePoint === NEWLINE_CODEPOINT) {
    return NEWLINE;
  }

  // Replace tabs to 2 spaces
  if (codePoint === TAB_CODEPOINT) {
    return SPACE + SPACE;
  }

  // Keep the Unicode replacement character
  // Validation is handled by `validateLatin1SafeInput`
  if (codePoint === REPLACEMENT_CHARACTER_CODEPOINT) {
    return REPLACEMENT_CHARACTER;
  }

  // Replace known legacy replacements with ASCII
  const legacyReplacement = getLegacyPasteReplacement(codePoint);
  if (legacyReplacement !== undefined) {
    return legacyReplacement;
  }

  // Remove invisible soft hyphen
  if (codePoint === 0x00ad) {
    return null;
  }

  // Replace NBSP to SPACE
  if (codePoint === 0x00a0) {
    return SPACE;
  }

  // Remove control characters (after we handled new-line, tab)
  if (isControlCharacter(codePoint)) {
    return null;
  }

  // Replace smart typography with ASCII
  const typography = getTypographyReplacement(codePoint);
  if (typography !== undefined) {
    return typography;
  }

  if (codePoint > LATIN1_MAX_CODEPOINT) {
    return sanitizeNonLatin1Character(inputChar);
  }

  // Keep as-is (safe for `latin1_swedish_ci` collation)
  return inputChar;
};

export const sanitizeLatin1SafeInput = (input?: MaybeString): SanitizeResult => {
  const inputToSanitize = input ?? '';
  // Normalisation doesn't count towards `didSanitize`, so run before sanitisation
  const cleanedInput = normalizeInputFormatting(inputToSanitize);

  const result = Array.from(cleanedInput)
    .map(sanitizeCharacter)
    .filter((ch): ch is string => ch !== null)
    .join('');

  return {
    value: result,
    didSanitize: result !== cleanedInput,
    didNormalize: cleanedInput !== inputToSanitize,
  };
};
